import Vue from 'vue'
import promoData from '@/data/promo.json'
import optionsData from '@/data/options.json'

const CHECK_INTERVAL_MS = 60 * 1000

function localDateString() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function deriveFreeProductActive() {
  const result = !!(optionsData.free_offer === '1' && optionsData.freeproduct_id)
  // console.log('[campaign] deriveFreeProductActive:', result, '| free_offer:', optionsData.free_offer, '| freeproduct_id:', optionsData.freeproduct_id)
  return result
}

// Pick the campaign whose date window covers today, from the scheduled queue.
// Falls back to the legacy single `activeCampaign` field for older builds.
// Consecutive campaigns (one ends, the next starts the following day) hand over
// automatically here because the check runs against the user's local date.
function selectActiveCampaign(today) {
  const list = Array.isArray(promoData.campaigns) ? promoData.campaigns : null

  if (list && list.length) {
    // Find the one covering today. If dates were misconfigured to overlap,
    // the earliest match wins (the list is already in date order from the plugin).
    for (let i = 0; i < list.length; i++) {
      const c = list[i]
      if (c && c.slug && c.startDate && c.endDate && today >= c.startDate && today <= c.endDate) {
        return c
      }
    }
    return null // queue exists but nothing is live right now
  }

  // Legacy fallback — single campaign field, still needs a date check
  const legacy = promoData.activeCampaign
  if (legacy && legacy.slug && legacy.startDate && legacy.endDate
      && today >= legacy.startDate && today <= legacy.endDate) {
    return legacy
  }
  return null
}

function deriveCampaignState() {
  const today             = localDateString()
  const campaign          = selectActiveCampaign(today)
  const freeProductActive = deriveFreeProductActive()

  // console.log('[campaign] selected campaign:', campaign)

  const hasBigCampaign = !!(
    campaign &&
    campaign.slug &&
    campaign.startDate &&
    campaign.endDate
  )

  // console.log('[campaign] hasBigCampaign:', hasBigCampaign)

  if (!hasBigCampaign) {
    const state = {
      isActive: freeProductActive,
      slug: null,
      startDate: null,
      endDate: null,
      bundle: null,
      _offersMap: {},
      _freeProductActive: freeProductActive,
    }
    // console.log('[campaign] no big campaign, returning state:', state)
    return state
  }

  // selectActiveCampaign already confirmed today is within the window
  const isActive = true

  // console.log('[campaign] today:', today, '| startDate:', campaign.startDate, '| endDate:', campaign.endDate)

  const offersMap = {}
  if (Array.isArray(campaign.offers)) {
    campaign.offers.forEach(function(item) {
      if (item && item.code) offersMap[item.code] = item.offer || null
    })
  }

  // console.log('[campaign] offersMap:', offersMap)

  // Expose the campaign's bundle override (if any) so banners can react to it.
  // Null when the live campaign has no bundle code set.
  let bundle = null
  if (campaign.bundleCode) {
    bundle = {
      code:   campaign.bundleCode,
      amount: campaign.bundleAmount,
      type:   campaign.bundleType || 'percentage',
    }
  }

  const state = {
    isActive: isActive || freeProductActive,
    slug:      campaign.slug,
    startDate: campaign.startDate,
    endDate:   campaign.endDate,
    bundle,
    _offersMap: offersMap,
    _freeProductActive: freeProductActive,
  }

  // console.log('[campaign] returning state:', state)
  return state
}

export default function (context, inject) {
  const state = Vue.observable(deriveCampaignState())

  state.offerForRegion = function(regionCode) {
    // Normalise to the codes _offersMap is keyed by (us/en_us → en-us, uk → en)
    let rc = (regionCode || 'en').toLowerCase().trim()
    if (rc === 'uk') rc = 'en'
    else if (rc === 'us' || rc === 'en_us') rc = 'en-us'

    const regionOffer = state._offersMap[rc]
    // A meaningful offer, not just an empty object
    const bigCampaignIsLive = state.isActive && state.slug && regionOffer && regionOffer.offerText
    if (bigCampaignIsLive) return regionOffer
    if (state._freeProductActive) return 'free-product'
    return null
  }

  inject('campaign', state)

  // Re-evaluate every minute so the switch from one campaign to the next (and the
  // start/end of any campaign) happens at the user's local midnight without a reload.
  setInterval(function() {
    const next = deriveCampaignState()
    next.offerForRegion = state.offerForRegion
    Object.assign(state, next)
  }, CHECK_INTERVAL_MS)
}